/** Indian Standard Time — used for all user-facing dates in P3M. */
export const IST_TIMEZONE = 'Asia/Kolkata';

type DateInput = Date | string | number | null | undefined;

function parseDate(input: DateInput): Date | null {
    if (input == null || input === '') return null;
    const d = input instanceof Date ? input : new Date(input);
    return Number.isNaN(d.getTime()) ? null : d;
}

const withIST = (
    options: Intl.DateTimeFormatOptions
): Intl.DateTimeFormatOptions => ({
    timeZone: IST_TIMEZONE,
    ...options,
});

/**
 * Full date + time in IST for errors, notifications, and UI copy.
 * Example: `30/6/2024, 1:33:01 pm IST`
 */
export function formatDateTimeIST(
    input: DateInput,
    fallback = 'unknown time'
): string {
    const d = parseDate(input);
    if (!d) return fallback;
    return `${d.toLocaleString('en-IN', withIST({
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        second: '2-digit',
        hour12: true,
    }))} IST`;
}

/**
 * Calendar date in IST. Pass optional Intl options for format variants.
 */
export function formatDateIST(
    input: DateInput,
    options: Intl.DateTimeFormatOptions = {},
    fallback = '-'
): string {
    const d = parseDate(input);
    if (!d) return fallback;
    return d.toLocaleDateString('en-IN', withIST(options));
}

/** YYYY-MM-DD calendar key in IST (for grouping, not display). */
export function toISTDateKey(input: DateInput): string | null {
    const d = parseDate(input);
    if (!d) return null;
    return d.toLocaleDateString('en-CA', withIST({
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }));
}

/** Today's YYYY-MM-DD key in IST. */
export function todayISTDateKey(): string {
    return toISTDateKey(new Date()) ?? '';
}

/** Yesterday's YYYY-MM-DD key in IST. */
export function yesterdayISTDateKey(): string {
    const d = parseDate(new Date());
    if (!d) return '';
    d.setDate(d.getDate() - 1);
    return toISTDateKey(d) ?? '';
}
